/**
 * @fileoverview Logo file state and upload handlers for the Corporate Identity section.
 */

import { useState } from "react";
import { createFileObjectFromUrl } from "../../../utils/common";

const LOGO_ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const LOGO_MAX_SIZE = 5 * 1024 * 1024; // 5MB

function notifyLogoChange(onFieldChange, value) {
  if (onFieldChange) {
    onFieldChange("logo", value);
  }
}

function applySelectedLogo(file, { t, setError, setLogoFile, onFieldChange }) {
  if (!LOGO_ALLOWED_TYPES.includes(file.type)) {
    setError(t("components.corporateIdentity.logoFileTypeError"));
    setLogoFile(null);
    notifyLogoChange(onFieldChange, null);
    return;
  }

  if (file.size > LOGO_MAX_SIZE) {
    setError(t("components.corporateIdentity.logoFileSizeError"));
    setLogoFile(null);
    notifyLogoChange(onFieldChange, null);
    return;
  }

  setLogoFile(file);
  setError("");
  notifyLogoChange(onFieldChange, file);
}

export function useCorporateIdentityLogo({ company, onFieldChange, t }) {
  const [logoFile, setLogoFile] = useState(
    createFileObjectFromUrl(company?.logo) || null,
  );
  const [isUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      applySelectedLogo(file, { t, setError, setLogoFile, onFieldChange });
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      applySelectedLogo(file, { t, setError, setLogoFile, onFieldChange });
    }
  };

  const handleFileClick = () => {
    document.getElementById("logoFileInput").click();
  };

  return {
    logoFile,
    isUploading,
    isDragging,
    error,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleFileClick,
  };
}
